import { cn } from "@/lib/utils";
import type { PromoKind, PropsPromo } from "@/lib/odds-screen/live/types";
import type { DisplayPlayerProp } from "@/components/optimizer/utils-v2";
import { formatMultiple, type PlayLine } from "./model";

const ICONS: Readonly<Record<PromoKind, string>> = {
  gimme: "/images/promos/dkpick6/gimme.png",
  slice: "/images/promos/dkpick6/stat-slice.png",
  power: "/images/promos/dkpick6/power-up.png",
  taco: "/images/promos/prizepicks/taco.png",
  hotdog: "/images/promos/prizepicks/hotdog.png",
  flash: "/images/promos/prizepicks/flash-sale.png",
  free: "/images/promos/prizepicks/free-square.png",
};

export function PromoIcon({ promo, size = 14, className }: { promo: Pick<PropsPromo, "kind" | "name">; size?: number; className?: string }) {
  return <img src={ICONS[promo.kind]} alt={promo.name} width={size} height={size} className={cn("flex-shrink-0", className)} style={{ width: size, height: size }} />;
}

export function PromoTag({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn("data-num inline-flex h-4 flex-shrink-0 items-center rounded bg-violet-500/[0.18] px-1 text-[10px] font-semibold leading-4 tracking-[0.02em] text-violet-300", className)}>{children}</span>;
}

export function capText(cap: number | null, long = false): string | null {
  return cap === null ? null : `Max${long ? " entry" : ""} $${Number.isInteger(cap) ? cap : cap.toFixed(2)}`;
}

export function picksText(promo: Pick<PropsPromo, "minPicks">): string | null {
  return promo.minPicks > 1 ? `${promo.minPicks}+ picks` : null;
}

export function promoMultiplier(row: DisplayPlayerProp): string | null {
  if (row.promo?.kind !== "power" || row.payout_multiplier === undefined) return null;
  return formatMultiple(row.payout_multiplier);
}

export function PromoStrip({ promo, className }: { promo: PropsPromo; className?: string }) {
  const cap = capText(promo.cap, true);
  const picks = picksText(promo);
  return (
    <div className={cn("flex h-9 items-center gap-2 bg-violet-500/[0.06] px-3.5", className)} role="note" aria-label={`${promo.name}${cap ? `, ${cap.toLowerCase()}` : ""}${picks ? `, ${picks}` : ""}`}>
      <PromoIcon promo={promo} size={16} />
      <span className="text-[13px] font-semibold text-foreground">{promo.name}</span>
      {cap && <PromoTag>{cap}</PromoTag>}
      {picks && <PromoTag>{picks}</PromoTag>}
    </div>
  );
}

export function PlayLineText({ play, className }: { play: PlayLine; className?: string }) {
  return (
    <span className={className}>
      {play.side} {play.line}
      {play.was !== null && <> <s className="text-zinc-500 decoration-zinc-500/70" aria-label={`was ${play.was}`}>{play.was}</s></>}
      {" · "}{play.market}
    </span>
  );
}
